import { useEffect, useMemo } from 'react';
import { DEFAULT_EVENT_ON } from '../api/util/constants';
import { getMainSensor } from '../api/util/sensor/getMainSensor';
import { DigitalChannelEventMixed } from '../../../redux/monitoring/api/monitoring.model';
import { filterAlarmsFromEvents } from '../../../redux/monitoring/api/util/filterAlarmsFromEvents';
import { getDigitalEventValue } from '../../../redux/monitoring/api/util/mapDigitalEvent';
import { useCUTranslate } from '../../CUTranslation/hook/useCUTranslate';
import { useAppSelector } from '../../store.model';
import { getActionExtendedSelectorById } from '../selectors/actions';
import { getSetPointsSelectorById } from '../selectors/setPoint';
import { useControlUnitById } from './useControlUnitById';
import { useCurrentEventsControlUnitById } from './useCurrentEventsControlUnitById';
import { useCurrentSensorDataControlUnitById } from './useCurrentSensorDataControlUnitById';
import { getStatusExtendedSelectorById } from '../selectors/status';
import { useDevice } from '../../commander/hooks/useDevice';
import CUActionsAPI from '../../CUActions/api/CUActionsAPI';
import { CU_POLLING_INTERVAL } from '../../CUActions/api/CUActions.constants';
import useWatchError from '../../../handlingErrors/useWatchError';
import { getCUActionsResponseByActionId } from '../../CUActions/selectors/CUActionsAPISelectors';
import { getColorFromSensor } from '../api/util/sensor/getColorFromSensor';
import { useColorStatusFromCU } from '../api/util/sensor/getColorStatusFromCU';
import { useCUParameters } from '../../CUParameters/hooks/useCUParameters';
import useCommanderStatus from '../../commander/commander-status/hooks/useCommanderStatus';
import DeviceModel from '../../commander/api/DeviceModel';

/**
 * Get and fetch all the data needed from specific control unit, but loading all the controlUnits from devices related
 * @param deviceId
 * @param controlUnitId
 * @param onError
 * @returns {
    loading,
    currentEvents,
    currentAlarms,
    currentSensorData,
    hasAlarms,
    onEvent,
    mainSensor,
    type,
    CUTranslate,
  }
 */
export function useCurrentStateByControlUnitId(
  deviceId: string,
  controlUnitId: string,
  onError: (error: unknown) => unknown
) {
  const {
    data: device,
    error: devicesError,
    isLoading: loadingDevice,
  } = useDevice(deviceId);

  const {
    data: currentEvents,
    error: currentEventsError,
    isLoading: loadingCurrentEvents,
  } = useCurrentEventsControlUnitById(deviceId, controlUnitId);
  const { error: actionsCollectionError, isLoading: loadingActionsCollection } =
    CUActionsAPI.useGetActionStatusQuery(deviceId, {
      skip: !deviceId,
      pollingInterval: CU_POLLING_INTERVAL,
    });

  const {
    data: controlUnit,
    isLoading: isLoadingControlUnit,
    error: errorControlUnits,
  } = useControlUnitById(deviceId, controlUnitId);

  const {
    data: currentSensorData,
    error: currentSensorDataError,
    isLoading: loadingCurrentSensorData,
  } = useCurrentSensorDataControlUnitById(deviceId, controlUnitId);

  const filterAlarms = (event: DigitalChannelEventMixed) =>
    filterAlarmsFromEvents(event.metadata, event);

  const [CUTranslateDevice, loadingCUTranslate] = useCUTranslate(deviceId);

  // Alarms
  const currentAlarms = useMemo(() => {
    return Object.values(
      (currentEvents as Record<string, DigitalChannelEventMixed>) ?? {}
    ).filter(filterAlarms);
  }, [currentEvents]);

  // Sensor
  const mainSensor = getMainSensor(currentSensorData);

  const hasAlarms = currentAlarms.length;
  const onEvent = currentEvents?.[DEFAULT_EVENT_ON];
  const onActionCollection = useAppSelector((state) =>
    getCUActionsResponseByActionId(state, {
      actionId: DEFAULT_EVENT_ON,
      deviceId: deviceId,
      controlUnitId,
    })
  );

  // Device status
  const {
    commanderStatus,
    isLoading: loadingCommanderStatus,
    error: commanderStatusError,
  } = useCommanderStatus(deviceId);
  const cloudConnectivityStatus = new DeviceModel().cloudConnectivityStatus(
    device?.lastPing,
    commanderStatus
  );

  const isOn = DeviceModel.getLatestDigitalValue(
    onEvent,
    onActionCollection?.data
  );

  const color = getColorFromSensor(
    loadingCurrentSensorData || loadingDevice,
    hasAlarms,
    isOn,
    cloudConnectivityStatus.cloudConnectivity
  );
  const colorStatus = useColorStatusFromCU(
    loadingCurrentSensorData || loadingDevice,
    hasAlarms,
    isOn,
    cloudConnectivityStatus.cloudConnectivity
  );

  // SetPoints
  const { error: errorParameter, loading: loadingParameters } = useCUParameters(
    { deviceId, controlUnitId },
    onError
  );
  const setPoints = useAppSelector((state) =>
    getSetPointsSelectorById(state, {
      deviceId,
      controlUnitId,
    })
  );

  // Actions
  const actions = useAppSelector((state) =>
    getActionExtendedSelectorById(state, {
      deviceId,
      controlUnitId,
    })
  );

  // Status
  const status = useAppSelector((state) =>
    getStatusExtendedSelectorById(state, {
      deviceId,
      controlUnitId,
    })
  );

  const CUTranslate = (keyTranslation: string, ...otherArgs: any[]) =>
    CUTranslateDevice(keyTranslation, controlUnitId, ...otherArgs);

  const loading = !!(
    loadingDevice ||
    isLoadingControlUnit ||
    loadingCurrentEvents ||
    loadingCurrentSensorData ||
    loadingCUTranslate ||
    loadingActionsCollection ||
    loadingParameters ||
    loadingCommanderStatus
  );

  const error =
    currentEventsError ||
    errorControlUnits ||
    currentSensorDataError ||
    errorParameter ||
    actionsCollectionError ||
    commanderStatusError ||
    devicesError;

  useWatchError(error);

  return {
    loading,
    error,
    controlUnit,
    currentEvents,
    currentAlarms,
    currentSensorData,
    setPoints,
    hasAlarms,
    actions,
    status,
    isOn,
    onEvent,
    mainSensor,
    color,
    colorStatus,
    cloudConnectivity: cloudConnectivityStatus.cloudConnectivity,
    CUTranslate,
  };
}
